import { useEffect, useMemo } from 'react';
import { Link } from 'react-router';
import type { MyTaskEntry } from '../../../data/client';
import { useMyTasks, useUpdateTask } from '../../../data/hooks';
import {
  Checkbox,
  cx,
  ExpandableText,
  ExpandableTextToggle,
  Icon,
  STATUS_INK,
  StatusSymbol,
  useExpandableText,
  useToast,
} from '../../../ui';
import { clientHref } from '../shared';
import { dueWords, type TaskLists } from './model';

/**
 * THE TEAM'S TASKS FOR TODAY: "We owe the client" and "Follow up with the client" (brief
 * section 6; mockup `:240-252`).
 *
 * THE TEAM'S TASKS, NOT MINE. The feed is the my-tasks route with `assignee: everyone`, open
 * only, due today or earlier; the route already omits a task on a record this caller may not
 * read (notes-tasks-comments D9). `model.ts` `taskLists` splits it: tasks on a client into the
 * two lists, every other task into the quiet "<N> other tasks due" line (register H2).
 *
 * EVERY PAGE, up to a ceiling. The route filters visibility AFTER reading a page, so a page
 * can come back short with a cursor (hooks.ts, `useMyTasks`); the lists are only right once
 * the pages are in.
 */
const TASK_PAGE = 200;
const MAX_PAGES = 5;

export function useTeamTasks(today: string): {
  readonly entries: readonly MyTaskEntry[];
  readonly isPending: boolean;
} {
  const tasks = useMyTasks({ assignee: 'everyone', open: true, limit: TASK_PAGE, dueTo: today });
  const pages = tasks.data?.pages.length ?? 0;
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = tasks;
  useEffect(() => {
    if (hasNextPage === true && !isFetchingNextPage && pages < MAX_PAGES) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, pages, fetchNextPage]);
  const entries = useMemo(
    () => (tasks.data?.pages ?? []).flatMap((page) => page.tasks),
    [tasks.data],
  );
  return { entries, isPending: tasks.isPending };
}

export function Commitments({
  lists,
  today,
}: {
  readonly lists: TaskLists<MyTaskEntry>;
  readonly today: string;
}) {
  return (
    <>
      <section aria-label="We owe the client" data-commitments="open_item">
        <h3 className="mb-2 text-sm font-semibold text-ink">We owe the client</h3>
        {lists.owe.length === 0 ? (
          <p className="text-sm text-ink-subtle">Nothing due today.</p>
        ) : (
          <TaskRows entries={lists.owe} today={today} />
        )}
      </section>
      <section aria-label="Follow up with the client" data-commitments="follow_up" className="mt-8">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
          Follow up with the client
          {lists.lateFollowUps > 0 && (
            <span className={cx('ms-auto text-2xs font-semibold', STATUS_INK.late)}>
              {`${String(lists.lateFollowUps)} late`}
            </span>
          )}
        </h3>
        {lists.followUp.length === 0 ? (
          <p className="text-sm text-ink-subtle">Nothing due today.</p>
        ) : (
          <TaskRows entries={lists.followUp} today={today} />
        )}
      </section>
    </>
  );
}

function TaskRows({
  entries,
  today,
}: {
  readonly entries: readonly MyTaskEntry[];
  readonly today: string;
}) {
  return (
    <ul className="divide-y divide-line-subtle border-t border-line-subtle">
      {entries.map((entry) => (
        <TaskRow key={entry.task.id} entry={entry} today={today} />
      ))}
    </ul>
  );
}

/**
 * ONE TASK (mockup `:244-252`): the round tick OUTSIDE the link (L10), the client's name, how
 * late with the late symbol in the late tone, the chevron, the task title on the second line.
 * The tick writes through the product's optimistic task mutation and offers Undo in the toast
 * the tasks screen uses for the same act.
 */
function TaskRow({ entry, today }: { readonly entry: MyTaskEntry; readonly today: string }) {
  const { task, parent } = entry;
  const objectId = parent.access === 'visible' ? parent.objectId : '';
  const recordId = parent.access === 'visible' ? parent.recordId : '';
  const update = useUpdateTask(objectId, recordId);
  const toast = useToast();
  const done = task.doneAt !== null;
  const words = dueWords(task.dueDate, today);
  const late = !done && task.dueDate !== null && task.dueDate.slice(0, 10) < today;
  const name = parent.access === 'visible' ? (parent.label ?? parent.objectLabel) : '';
  const expandable = useExpandableText();

  const complete = (checked: boolean) => {
    update.mutate({ taskId: task.id, patch: { done: checked } });
    if (!checked) return;
    toast({
      title: 'Task completed',
      tone: 'success',
      action: {
        label: 'Undo',
        altText: 'Undo completing this task',
        onAction: () => update.mutate({ taskId: task.id, patch: { done: false } }),
      },
    });
  };

  return (
    <li className={cx('flex items-start gap-2.5', done && 'opacity-60')}>
      <span className="pt-2.5">
        <Checkbox
          appearance="circle"
          checked={done}
          onCheckedChange={complete}
          ariaLabel={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
        />
      </span>
      <div className="min-w-0 flex-1">
        <Link
          to={clientHref(recordId)}
          className="pressable -mx-2 grid min-h-(--touch-target-min) min-w-0 cursor-default grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-x-3 gap-y-0.5 px-2 py-2.5 hover:bg-surface-hover"
        >
          <span className="min-w-0 font-semibold text-ink">{name}</span>
          <span className="text-sm tabular-nums">
            {words !== null &&
              (late ? (
                <span
                  className={cx('inline-flex items-center gap-1 font-semibold', STATUS_INK.late)}
                >
                  <StatusSymbol status="late" />
                  {words}
                </span>
              ) : (
                <span className="text-ink-muted">{words}</span>
              ))}
          </span>
          <Icon name="chevron-right" className="self-center text-ink-subtle" />
          <span className="col-span-2 text-2xs text-ink-muted">
            <ExpandableText lines={2} state={expandable}>
              {task.title}
            </ExpandableText>
          </span>
        </Link>
        {/* "More" is a button, so it stands under the link and never inside it; it names the
            row's client so the Dashboard's several "More" buttons differ (BR-PARTS-18). */}
        <ExpandableTextToggle
          state={expandable}
          subject={name === '' ? undefined : name}
          className="-mt-1.5 mb-1.5"
        />
      </div>
    </li>
  );
}
